import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Phone, Mail, MapPin, Clock, CheckCircle, Building2, Battery, Sun, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const projectTypes = [
  { id: 'schwarmspeicher', name: 'Kleinspeicher / Schwarmspeicher', icon: Zap },
  { id: 'bess', name: 'Batteriespeicher', icon: Battery },
  { id: 'freiflaeche', name: 'Freiflächen-PV', icon: Sun },
  { id: 'dach', name: 'Dachverpachtung', icon: Building2 },
];

const sizeOptions = [
  { value: 'klein', label: 'Klein (< 500m² / < 1ha)' },
  { value: 'mittel', label: 'Mittel (500-2.000m² / 1-5ha)' },
  { value: 'gross', label: 'Groß (> 2.000m² / > 5ha)' },
  { value: 'unbekannt', label: 'Noch nicht bekannt' },
];

export const AngebotAnfordern = () => {
  const [formData, setFormData] = useState({
    projectType: '',
    name: '',
    email: '',
    phone: '',
    company: '',
    size: '',
    location: '',
    message: '',
    privacy: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const selectedProject = projectTypes.find((type) => type.id === formData.projectType)?.name || formData.projectType;
    const body = [
      'Neue Anfrage über en-power.eu',
      '',
      `Projektart: ${selectedProject}`,
      `Name: ${formData.name}`,
      `E-Mail: ${formData.email}`,
      `Telefon: ${formData.phone || '-'}`,
      `Unternehmen: ${formData.company || '-'}`,
      `Flächengröße: ${formData.size || '-'}`,
      `Standort: ${formData.location || '-'}`,
      '',
      'Nachricht:',
      formData.message || '-',
    ].join('\n');

    const mailtoUrl = `mailto:info@en-power.eu?subject=${encodeURIComponent(`EN:POWER Anfrage - ${selectedProject}`)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;

    await new Promise(resolve => setTimeout(resolve, 500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-50 py-20 md:py-28 flex items-center" data-testid="success-page">
        <div className="max-w-lg mx-auto px-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-500" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="font-heading text-3xl font-bold text-[#122A5C] mb-4">
              Ihre Anfrage wurde vorbereitet.
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Ihr E-Mail-Programm wurde mit allen Angaben geöffnet. Bitte senden Sie die vorbereitete Nachricht ab.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-[#E07000] hover:bg-[#C56200] text-white rounded-full px-6 py-3"
              data-testid="back-home-button"
            >
              Zurück zur Startseite
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 md:py-24" data-testid="angebot-page">
      {/* Header */}
      <div className="mx-auto mb-8 max-w-7xl px-4 sm:px-6 md:mb-12 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4">
            Kontakt
          </span>
          <h1 className="font-heading mb-4 text-3xl font-bold text-[#122A5C] md:text-4xl lg:text-5xl">
            Jetzt unverbindlich anfragen
          </h1>
          <p className="mx-auto max-w-2xl text-base text-slate-600 md:text-lg">
            Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen. 
            Die Erstberatung ist selbstverständlich kostenlos.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <Card className="overflow-hidden rounded-lg border-0 shadow-xl md:rounded-3xl">
              <CardHeader className="bg-[#122A5C] text-white p-6">
                <CardTitle className="flex items-center gap-3 font-heading text-xl">
                  <Send className="w-6 h-6" />
                  Anfrage-Formular
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Project Type Selection */}
                  <div>
                    <Label className="text-sm font-semibold text-[#122A5C] mb-4 block">
                      Wofür interessieren Sie sich? *
                    </Label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
                      {projectTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => handleInputChange('projectType', type.id)}
                          data-testid={`project-type-${type.id}`}
                          className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-all md:block md:p-4 md:text-center ${
                            formData.projectType === type.id
                              ? 'border-[#E07000] bg-[#E07000]/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <type.icon className={`h-6 w-6 flex-shrink-0 md:mx-auto md:mb-2 ${
                            formData.projectType === type.id ? 'text-[#E07000]' : 'text-slate-400'
                          }`} />
                          <div className="font-medium text-[#122A5C] text-sm">{type.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        required
                        placeholder="Max Mustermann"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="rounded-xl"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        E-Mail *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        placeholder="max@beispiel.de"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="rounded-xl"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Telefon
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+49 123 456789"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="rounded-xl"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Unternehmen
                      </Label>
                      <Input
                        id="company"
                        placeholder="Firma GmbH"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="rounded-xl"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Flächengröße
                      </Label>
                      <Select 
                        value={formData.size} 
                        onValueChange={(val) => handleInputChange('size', val)}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="select-size">
                          <SelectValue placeholder="Bitte auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="location" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                        Standort (PLZ/Ort)
                      </Label>
                      <Input
                        id="location"
                        placeholder="z.B. 10115 Berlin"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        className="rounded-xl"
                        data-testid="input-location"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-[#122A5C] mb-2 block">
                      Ihre Nachricht
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Erzählen Sie uns mehr über Ihr Projekt..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="rounded-xl min-h-[120px]"
                      data-testid="input-message"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="privacy"
                        required
                        checked={formData.privacy}
                        onCheckedChange={(checked) => handleInputChange('privacy', checked)}
                        data-testid="checkbox-privacy"
                      />
                      <Label htmlFor="privacy" className="text-sm text-slate-600 cursor-pointer">
                        Ich habe die <a href="/datenschutz" className="text-[#E07000] hover:underline">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Daten zu. *
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit"
                    disabled={isSubmitting || !formData.privacy || !formData.projectType}
                    className="w-full bg-[#E07000] hover:bg-[#C56200] text-white rounded-full py-6 text-lg font-semibold btn-animate disabled:opacity-50"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner mr-2" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        Anfrage absenden
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info Sidebar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            {/* Direct Contact */}
            <Card className="rounded-lg border-0 shadow-lg md:rounded-2xl">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[#122A5C] mb-6">
                  Direkter Kontakt
                </h3>
                <div className="space-y-4">
                  <a href="tel:+491608732681" className="flex items-center gap-4 text-slate-600 hover:text-[#E07000] transition-colors group">
                    <div className="w-10 h-10 bg-[#E07000]/10 rounded-xl flex items-center justify-center group-hover:bg-[#E07000] transition-colors">
                      <Phone className="w-5 h-5 text-[#E07000] group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Telefon</div>
                      <div className="font-medium">+49 160 8732681</div>
                    </div>
                  </a>
                  <a href="mailto:info@en-power.eu" className="flex items-center gap-4 text-slate-600 hover:text-[#E07000] transition-colors group">
                    <div className="w-10 h-10 bg-[#E07000]/10 rounded-xl flex items-center justify-center group-hover:bg-[#E07000] transition-colors">
                      <Mail className="w-5 h-5 text-[#E07000] group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">E-Mail</div>
                      <div className="font-medium">info@en-power.eu</div>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="rounded-lg border-0 shadow-lg md:rounded-2xl">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[#122A5C] mb-6">
                  Unser Büro
                </h3>
                <div className="flex items-start gap-4 text-slate-600">
                  <div className="w-10 h-10 bg-[#E07000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#E07000]" />
                  </div>
                  <div>
                    <div className="font-medium">Kirsten und Klein eGbR Roofshare Solutions</div>
                    <div className="text-sm">handelnd unter der Bezeichnung EN:POWER</div>
                    <div className="text-sm">
                      Haferfeld 3<br />
                      06485 Quedlinburg<br />
                      Deutschland
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card className="rounded-lg border-0 shadow-lg md:rounded-2xl">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[#122A5C] mb-6">
                  Erreichbarkeit
                </h3>
                <div className="flex items-start gap-4 text-slate-600">
                  <div className="w-10 h-10 bg-[#E07000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#E07000]" />
                  </div>
                  <div className="text-sm">
                    <div className="font-medium mb-2">Öffnungszeiten</div>
                    <div>Mo - Fr: 08:00 - 18:00</div>
                    <div>Sa: Nach Vereinbarung</div>
                    <div>So: Geschlossen</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Guarantee */}
            <Card className="rounded-lg border-0 bg-[#122A5C] shadow-lg md:rounded-2xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#E07000] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">
                  Unser Versprechen
                </h3>
                <p className="text-white/80 text-sm">
                  Kostenlose Erstberatung. Unverbindliches Angebot. Antwort innerhalb von 24 Stunden.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
